import { z } from 'zod'

export const inOutSchema = z.enum(['in', 'out']).nullable().catch(null)
export const upDownSchema = z.enum(['up', 'down']).nullable().catch(null)

export type TE = z.infer<typeof inOutSchema>
export type BA = z.infer<typeof upDownSchema>

// Loose: the ERP owns this shape, we only declare what the app reads or writes.
// Anything else has to survive the round trip untouched or an order never stops
// looking changed, see orderCompare
export const refractionSchema = z.looseObject({
  r_sph: z.coerce.number().catch(0),
  l_sph: z.coerce.number().catch(0),
  r_cyl: z.coerce.number().catch(0),
  l_cyl: z.coerce.number().catch(0),
  r_axis: z.coerce.number().catch(0),
  l_axis: z.coerce.number().catch(0),
  r_add: z.coerce.number().catch(0),
  l_add: z.coerce.number().catch(0),
  r_visus: z.coerce.number().catch(0),
  l_visus: z.coerce.number().catch(0),
  r_pd: z.coerce.number().catch(0),
  l_pd: z.coerce.number().catch(0),
  prism: z.coerce.boolean().catch(false),
  r_prism1: z.coerce.number().catch(0),
  l_prism1: z.coerce.number().catch(0),
  r_prism2: z.coerce.number().catch(0),
  l_prism2: z.coerce.number().catch(0),
  // Some very old prescriptions have invalid values for na_te and ha_ba
  right_na_te: inOutSchema,
  right_ha_ba: upDownSchema,
  left_na_te: inOutSchema,
  left_ha_ba: upDownSchema,
  optometrist: z.string().or(z.number()).nullable().catch(null),
})

export const orderRefractionSchema = refractionSchema.extend({
  x_ext: z.number().nullable().catch(0),
  y_ext: z.number().nullable().catch(0),

  r_base_curve: z.number().nullish().catch(undefined),
  l_base_curve: z.number().nullish().catch(undefined),
  r_decentration: z.number().nullish().catch(undefined),
  l_decentration: z.number().nullish().catch(undefined),
  r_et: z.number().nullish().catch(undefined),
  l_et: z.number().nullish().catch(undefined),
  r_ct: z.number().nullish().catch(undefined),
  l_ct: z.number().nullish().catch(undefined),
  ffa: z.number().nullish().catch(undefined),
  pa: z.number().nullish().catch(undefined),
  bvd: z.number().nullish().catch(undefined),
  rd: z.number().nullish().catch(undefined),
  thickness_balance: z.boolean().nullish().catch(undefined),
})

type NumberStep = 0.01 | 0.1 | 0.25 | 0.5 | 1

export type NumberInputSchema = {
  min: number
  max: number
  default?: number
  // Those are the only valid values for step for now
  step: NumberStep
  step_picker?: NumberStep
  allowPartial?: boolean
}

// This allows us to validate the refraction form inputs outside of zod
export const RefractionValidations: Record<string, NumberInputSchema> = {
  sph: {
    min: -16,
    max: 16,
    step: 0.25,
  },
  cyl: {
    min: -6,
    max: 0,
    step: 0.25,
  },
  axis: {
    min: 0,
    max: 180,
    step: 1,
  },
  add: {
    min: 0,
    max: 4,
    step: 0.25,
  },
  visus: {
    min: 0,
    max: 2,
    step: 0.1,
  },
  pd: {
    min: 22,
    max: 45,
    default: 30,
    step: 0.01,
    step_picker: 0.25,
    allowPartial: true, // otherwise validation breaks cuz min > single digit
  },
  prism1: {
    min: 0,
    max: 6,
    step: 0.25,
  },
  prism2: {
    min: 0,
    max: 6,
    step: 0.25,
  },
}

export const LensCustomizationValidations: Record<string, NumberInputSchema> = {
  ffa: {
    min: 0,
    max: 20,
    step: 1,
  },
  pa: {
    min: 0,
    max: 20,
    step: 1,
  },
  bvd: {
    min: 5,
    max: 20,
    step: 1,
  },
  rd: {
    min: 20,
    max: 60,
    step: 1,
  },
  base_curve: {
    min: 0,
    max: 8,
    step: 1,
  },
  decentration: {
    min: 0,
    max: 5,
    step: 0.5,
  },
  ct: {
    min: 0,
    max: 10,
    step: 0.1,
  },
  et: {
    min: 0,
    max: 10,
    step: 0.1,
  },
}

export const makeZodNumber = (schema: NumberInputSchema) => {
  let zod = z.coerce.number()
  if (schema.min != null) {
    zod = zod.min(schema.min)
  }
  if (schema.max != null) {
    zod = zod.max(schema.max)
  }
  if (schema.step != null) {
    zod = zod.multipleOf(schema.step)
  }
  return zod
}

export const makeNullishZodNumber = (schema: NumberInputSchema) => {
  const num = makeZodNumber(schema).nullish()
  return z.preprocess((val) => (val === '' ? undefined : val), num)
}

// Create Zod number schemas for each refraction input type
export const ZodRefractionValidations = Object.fromEntries(
  Object.entries(RefractionValidations).map(
    ([key, schema]) => [key, makeZodNumber(schema).default(0)] as const
  )
) as Record<keyof typeof RefractionValidations, z.ZodDefault<z.ZodNumber>>

export const CustomizationValidationsZod = Object.fromEntries(
  Object.entries(LensCustomizationValidations).map(
    ([key, schema]) => [key, makeNullishZodNumber(schema)] as const
  )
)

/**
 * Also used to validate auto measurement pd so needs step 0.01
 */
export const pdSchema = z.object({
  l_pd: ZodRefractionValidations.pd,
  r_pd: ZodRefractionValidations.pd,
})

export const refractionFormSchema = z.object({
  r_sph: ZodRefractionValidations.sph,
  l_sph: ZodRefractionValidations.sph,
  r_cyl: ZodRefractionValidations.cyl,
  l_cyl: ZodRefractionValidations.cyl,
  r_axis: ZodRefractionValidations.axis,
  l_axis: ZodRefractionValidations.axis,
  r_add: ZodRefractionValidations.add,
  l_add: ZodRefractionValidations.add,
  r_visus: ZodRefractionValidations.visus,
  l_visus: ZodRefractionValidations.visus,
  r_pd: ZodRefractionValidations.pd,
  l_pd: ZodRefractionValidations.pd,
  prism: z.coerce.boolean().catch(false),
  r_prism1: ZodRefractionValidations.prism1,
  l_prism1: ZodRefractionValidations.prism1,
  r_prism2: ZodRefractionValidations.prism2,
  l_prism2: ZodRefractionValidations.prism2,
  // Some very old prescriptions have invalid values for na_te and ha_ba
  right_na_te: inOutSchema.nullable().catch(null),
  right_ha_ba: upDownSchema.nullable().catch(null),
  left_na_te: inOutSchema.nullable().catch(null),
  left_ha_ba: upDownSchema.nullable().catch(null),

  remark: z.string().catch(''),
  optometrist: z.number(),
})

export const LensCustomizationSchema = z.object({
  corridor: z.string().or(z.number()).nullish().catch(null),
  ffa: CustomizationValidationsZod.ffa,
  pa: CustomizationValidationsZod.pa,
  bvd: CustomizationValidationsZod.bvd,
  rd: CustomizationValidationsZod.rd,
  l_base_curve: CustomizationValidationsZod.base_curve,
  r_base_curve: CustomizationValidationsZod.base_curve,
  l_decentration: CustomizationValidationsZod.decentration,
  r_decentration: CustomizationValidationsZod.decentration,
  l_ct: CustomizationValidationsZod.ct,
  r_ct: CustomizationValidationsZod.ct,
  l_et: CustomizationValidationsZod.et,
  r_et: CustomizationValidationsZod.et,
  thickness_balance: z.boolean().nullish().catch(null),
})

export type LensCustomization = z.infer<typeof LensCustomizationSchema>
export type Refraction = z.infer<typeof refractionSchema>
export type RefractionForm = z.infer<typeof refractionFormSchema>

export const prescriptionSchema = refractionSchema.extend({
  name: z.string().catch(''),
  remark: z.string().catch(''),
})

// No .strip() here, it would undo the looseness and drop the ERP owned fields
export const prescriptionResponseSchema = prescriptionSchema.extend({
  id: z.number().default(0),
  create_date: z.string(),
  optometrist: z.number().nullable().catch(null),
  optometrist_name: z.string().nullable().catch(null),
  // parent_handler: z.number().nullable(),
})

export const prescriptionCreateTag = {
  pd: 'CA_PD',
  manual: 'CA_manual',
  /**
   * @deprecated don't create new prescriptions with this tag
   * use pdUpdated instead
   * current backend excludes prescriptions with this tag since it starts with same value as CA_PD
   */
  pdAdded: 'CA_PD_added',
  pdUpdated: 'CA_updated_PD',
} as const

export const prescriptionCreateSchema = prescriptionSchema.extend({
  optometrist: z.number().nullable(),
  name: z
    .enum([
      prescriptionCreateTag.manual,
      prescriptionCreateTag.pdAdded,
      prescriptionCreateTag.pdUpdated,
      prescriptionCreateTag.pd,
    ])
    .default(prescriptionCreateTag.manual)
    .catch(prescriptionCreateTag.manual),
  // parent_handler: z.number().nullable(),
})

export type Prescription = z.infer<typeof prescriptionSchema>
export type PrescriptionResponse = z.infer<typeof prescriptionResponseSchema>
/** Normalized prescription data for read-only UI (prescriptions API or ERP order history). */
export type PrescriptionDisplayData = PrescriptionResponse
export type PrescriptionCreatePayload = z.infer<typeof prescriptionCreateSchema>

export type PrescriptionType = 'refbox' | 'manual' | 'pd' | 'other' | 'pdAdded'

const prescriptionTypeMatching: Record<PrescriptionType, readonly string[]> = {
  refbox: ['robox', 'refbox'],
  manual: [prescriptionCreateTag.manual],
  pd: [prescriptionCreateTag.pd],
  pdAdded: [prescriptionCreateTag.pdAdded, prescriptionCreateTag.pdUpdated],
  other: [],
} as const

export const prescriptionRemoteTags = ['robox', 'refbox']

export const getPrescriptionType = (
  name: string | undefined | null
): PrescriptionType => {
  if (!name) return 'other'

  const nameLC = name.toLowerCase()

  let match: PrescriptionType = 'other'
  for (const [key, tags] of Object.entries(prescriptionTypeMatching)) {
    // If exact match pick that
    if (tags.some((t) => nameLC === t.toLowerCase())) {
      return key as PrescriptionType
    }
    // Keep track of partial match
    if (tags.some((t) => nameLC.includes(t.toLowerCase()))) {
      match = key as PrescriptionType
    }
  }

  return match
}
